import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import { Signal } from '@/models/data/Signal';
import type {
  IChipEditorModel,
  IEditorModel,
  IEventBus,
  ITileManagerModel,
  IConnectionModel,
  IChipModel,
  IUiModel,
} from '@/interfaces/models';
import { TileType } from '@/models/TileTypes';
import { TileInstance } from '@/models/tiles/TileInstance';
import * as PIXI from 'pixi.js';
import { TileMetaSchema, Side, TileSideIOType } from '@/schemas/tiles';
import z from 'zod';
import { LogicGraph } from '@/models/logic/LogicGraph';
import { Direction } from '@/utils/direction';
import { GlobalTilePosition } from '@/types';
import { InterfaceTileDefinition } from './InterfaceTile';

export interface TileSideIO {
  type: TileSideIOType;
  forceConnection: boolean;
}

export type ConnectionFlags = Record<Side, boolean>;

export const allDisconnected: ConnectionFlags = {
  [Side.TOP]: false,
  [Side.RIGHT]: false,
  [Side.BOTTOM]: false,
  [Side.LEFT]: false,
};

export const allConnected: ConnectionFlags = {
  [Side.TOP]: true,
  [Side.RIGHT]: true,
  [Side.BOTTOM]: true,
  [Side.LEFT]: true,
};

export function getEffectiveSideIO(
  tile: TileInstance,
  side: Side,
  definition: TileDefinition<unknown>,
): TileSideIO {
  return tile.effectiveIoSides?.[side] ?? definition.ioSides[side];
}

export type ProcessFn<TState = null> = (data: {
  instance: TileInstance<TState>;
  eventBus: IEventBus;
}) => Record<Side, Signal | null>;

export type CreateDefaultStateData = {
  meta: TileMeta;
  globalTilePosition: GlobalTilePosition;
  eventBus?: IEventBus;
};
export type CreateDefaultStateFn<TState = null> = (
  data: CreateDefaultStateData,
) => TState;

export type Action = 'press' | 'depress' | 'toggle';
export type CreateActionsData<TState = null> = {
  instance: TileInstance<TState>;
  eventBus?: IEventBus;
};
export type CreateActionsFn<TState = null> = (
  data: CreateActionsData<TState>,
) => Partial<Record<Action, () => void>>;

export type CreateContextMenuItemsData<TState = null> = {
  instance: TileInstance<TState>;
  chipEditorModel: IChipEditorModel;
};
export enum InputType {
  STRING = 'string',
  MULTILINE = 'multiline',
  NUMBER = 'number',
  COLOR = 'color',
  SLIDER = 'slider',
}
export type ContextMenuActionFn = (
  takeInput: IUiModel['openGenericInputDialog'],
  uiModel: IUiModel,
) => void;
export type CreateContextMenuItemsFn<TState = null> = (
  data: CreateContextMenuItemsData<TState>,
) => Array<{
  label: string;
  action: ContextMenuActionFn;
}>;

export type EventHandlers<TState = null> = {
  placed?: (data: EventHandlerData<TState>) => void;
};
export type EventHandlerData<TState = null> = {
  interfaceLabel?: string;
  instance: TileInstance<TState>;
  chipEditorModel: IChipEditorModel;
};

export interface TileDefinition<TState = null> {
  type: TileType;

  name: string;

  canRotate: boolean;

  createDefaultState: CreateDefaultStateFn<TState>;
  /** Validate extension state when hydrating imported or saved tiles. */
  stateSchema?: z.ZodType<TState>;
  /** Return only topology/configuration data, excluding frequently changing runtime values. */
  graphConfiguration?: (state: TState) => unknown;
  /** Pure, chip-local wireless membership; compiled to ordinary graph edges. */
  wirelessChannel?: (state: TState) => { channel: string; direction: 'send' | 'receive' };
  createActions?: CreateActionsFn<TState>;
  eventHandlers?: EventHandlers<TState>;
  buildAndConnectNodes?: BuildAndConnectNodesFn<TState>;
  getGraphBuildVisitKey?: (instance: TileInstance, entryDirection?: Direction) => string;
  /** Opt in only for hooks that exclusively project graph state into tile visuals. */
  syncStateIsVisualOnly?: boolean;
  syncState?: (data: { instance: TileInstance<TState>; graph: LogicGraph }) => void;
  /** Rebuild-time eligibility; omitted keeps custom pre-sync hooks enabled. */
  needsStateToNodeSync?: (instance: TileInstance<TState>) => boolean;
  syncStateToNodes?: (data: {
    instance: TileInstance<TState>;
    graph: LogicGraph;
  }) => void;

  saveState?: (data: { instance: TileInstance<TState>; graph: LogicGraph }) => any;
  restoreState?: (data: {
    instance: TileInstance<TState>;
    graph: LogicGraph;
    savedState: any;
  }) => void;

  createContextMenuItems?: CreateContextMenuItemsFn<TState>;

  ioSides: Record<Side, TileSideIO>;
  graphics: TileGraphicsDefinition<TState>;
}

export interface TileGraphicsContext {
  showLabels?: boolean;
  interfaceLink?: import('@/models/ChipModel').InterfaceLink;
  isTileImageGeneration?: boolean;
  editor?: IEditorModel;
  hoveredTileId?: string;
}

export interface TileGraphicsDefinition<TState = null> {
  type: TileType;
  draw: (
    graphics: PIXI.Graphics,
    data: {
      meta: TileMeta;
      state: TState;
      connections: ConnectionFlags;
      chipEditorModel?: IChipEditorModel;
      context?: TileGraphicsContext & { mainChip?: IChipModel };
    },
  ) => void;
}

export type TileMeta = z.infer<typeof TileMetaSchema>;

export const createAllLowSignal = (): Record<Side, Signal.LOW> => ({
  [Side.TOP]: Signal.LOW,
  [Side.RIGHT]: Signal.LOW,
  [Side.BOTTOM]: Signal.LOW,
  [Side.LEFT]: Signal.LOW,
});

export const createAllHighSignal = (): Record<Side, Signal.HIGH> => ({
  [Side.TOP]: Signal.HIGH,
  [Side.RIGHT]: Signal.HIGH,
  [Side.BOTTOM]: Signal.HIGH,
  [Side.LEFT]: Signal.HIGH,
});

export interface GraphBuildQueueItem {
  tile: TileInstance<any>;
  sourceNodeId: NodeHandle;
  sourceTileId?: string;
  entryDirection: Direction;
}

export type BuildAndConnectNodesFn<TState = null> = (data: {
  graph: LogicGraph;
  tileManager: ITileManagerModel;
  connectionModel?: IConnectionModel;
  instance: TileInstance<TState>;
  sourceNodeId?: NodeHandle;
  sourceTileId?: string;
  entryDirection?: Direction;
}) => GraphBuildQueueItem[];

export const isInterfaceTileDefinition = (
  tileDef: TileDefinition<any>,
): tileDef is InterfaceTileDefinition<unknown> => {
  return (tileDef as InterfaceTileDefinition<unknown>).isInterface === true;
};
